import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { PermissaoMenuRequest, PermissaoMenuManutencaoMenuItem, PermissaoMenuManutencaoUsuario } from '../../../models/auth.model';
import { ErrorModel } from '../../../models/error.model';

interface MenuItemPermissao {
  menuItem: PermissaoMenuManutencaoMenuItem;
  children: MenuItemPermissao[];
  temPermissao: boolean;
  podeVisualizar: boolean;
  podeEditar: boolean;
  podeExcluir: boolean;
  salvando: boolean;
}

@Component({
  selector: 'app-permissoes-usuario-manutencao',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './permissoes-usuario-manutencao.component.html',
  styleUrls: ['./permissoes-usuario-manutencao.component.css']
})
export class PermissoesUsuarioManutencaoComponent implements OnInit {
  idUsuario = 0;
  usuario: PermissaoMenuManutencaoUsuario | null = null;
  menuTree: MenuItemPermissao[] = [];
  carregando = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.toastr.error('Usuário inválido.');
      this.voltar();
      return;
    }
    this.idUsuario = +id;
    this.loadDados();
  }

  loadDados(): void {
    this.carregando = true;
    this.authService.getPermissoesManutencao(this.idUsuario).subscribe({
      next: (data) => {
        this.usuario = data.usuario;
        this.menuTree = this.buildMenuTree(data.menus);
        this.carregando = false;
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao carregar dados de permissões.');
        this.carregando = false;
      }
    });
  }

  private buildMenuTree(menus: PermissaoMenuManutencaoMenuItem[]): MenuItemPermissao[] {
    const toNode = (item: PermissaoMenuManutencaoMenuItem): MenuItemPermissao => ({
      menuItem: item,
      children: [],
      temPermissao: item.podeVisualizar || item.podeEditar || item.podeExcluir,
      podeVisualizar: item.podeVisualizar,
      podeEditar: item.podeEditar,
      podeExcluir: item.podeExcluir,
      salvando: false
    });

    const nodeMap = new Map<number, MenuItemPermissao>();
    menus.forEach(m => nodeMap.set(m.menuItemId, toNode(m)));

    const roots: MenuItemPermissao[] = [];
    menus.forEach(m => {
      const node = nodeMap.get(m.menuItemId)!;
      if (m.parentId === null || m.parentId === 0) {
        roots.push(node);
      } else {
        const parent = nodeMap.get(m.parentId);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      }
    });

    const sort = (nodes: MenuItemPermissao[]) => {
      nodes.sort((a, b) => (a.menuItem.posicao ?? 0) - (b.menuItem.posicao ?? 0));
      nodes.forEach(n => sort(n.children));
    };
    sort(roots);

    return roots;
  }

  salvarPermissao(node: MenuItemPermissao): void {
    node.salvando = true;
    const payload: PermissaoMenuRequest = {
      usuarioId: this.idUsuario,
      menuItemId: node.menuItem.menuItemId,
      podeVisualizar: node.podeVisualizar,
      podeEditar: node.podeEditar,
      podeExcluir: node.podeExcluir,
      parentId: node.menuItem.parentId ?? 0,
      slug: node.menuItem.slug ?? '',
      title: node.menuItem.title
    };

    const request$ = node.temPermissao
      ? this.authService.updatePermissaoMenu(this.idUsuario, node.menuItem.menuItemId, payload)
      : this.authService.createPermissaoMenu(payload);

    request$.subscribe({
      next: () => {
        node.temPermissao = node.podeVisualizar || node.podeEditar || node.podeExcluir;
        node.salvando = false;
        this.toastr.success('Permissão salva com sucesso!');
      },
      error: (error) => {
        const erro: ErrorModel = error.error;
        this.toastr.error(erro?.message || 'Erro ao salvar permissão.');
        node.salvando = false;
      }
    });
  }

  onVisualizarChange(node: MenuItemPermissao): void {
    // Se remover visualizar, remove editar e excluir também
    if (!node.podeVisualizar) {
      node.podeEditar = false;
      node.podeExcluir = false;
    }
    this.salvarPermissao(node);
  }

  onEditarChange(node: MenuItemPermissao): void {
    // Editar requer visualizar
    if (node.podeEditar) {
      node.podeVisualizar = true;
    }
    this.salvarPermissao(node);
  }

  onExcluirChange(node: MenuItemPermissao): void {
    // Excluir requer visualizar
    if (node.podeExcluir) {
      node.podeVisualizar = true;
    }
    this.salvarPermissao(node);
  }

  voltar(): void {
    this.router.navigate(['/home', 'sistema-permissoes']);
  }
}
